import {
    APPROVAL_STATUS_TITLES,
    type ApprovalResponseStatus,
    parseClientApprovalAction,
} from '@common/enums';
import type { ApprovalResponse } from '@common/types';
import { Box, Button, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { type ReactNode, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getClientApprovalStatus } from '@/services/EmailApprovalService';
import useClientApprovalPageStore from '@/stores/useClientApprovalPageStore';

export const commonStyles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: '8px',
        margin: '0px',
    },
    buttonText: {
        width: 'max-content',
        textTransform: 'none',
        padding: '8px 20px',
    },
};

const textFieldRootMobile = {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
        fontSize: '14px',
        padding: '8px 12px 8px 12px',
    },
};

export function MobileLogo({
    isMobile,
    logoFileId,
}: {
    isMobile: boolean;
    logoFileId?: string | null;
}) {
    return (
        <Box data-metaInfo={'mobile-logo-container'}>
            {isMobile && logoFileId && (
                <img
                    alt="Law firm logo"
                    style={{
                        height: '80px',
                        width: '80px',
                        borderRadius: '4px',
                    }}
                    src={`${import.meta.env.VITE_APP_API_URL}/api/v1/files/${logoFileId}`}
                />
            )}
        </Box>
    );
}

export function LawyerInfo() {
    const [searchParams] = useSearchParams();
    const lawyerPhone = searchParams.get('lawyerPhone');
    const email = searchParams.get('lawyerEmail');
    const lawyerName = searchParams.get('lawyerName');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    return (
        <Box>
            <Typography
                variant="body2"
                color="text.primary"
                style={{
                    marginTop: '8px',
                    fontSize: isMobile ? '12px' : '14px',
                    lineHeight: isMobile ? '16px' : '20px',
                }}
            >
                Please contact {lawyerName || 'your assigned user'} if you have any questions.
            </Typography>
            {email && (
                <Typography
                    variant="body2"
                    color="text.primary"
                    style={{
                        fontSize: isMobile ? '12px' : '14px',
                        lineHeight: isMobile ? '16px' : '20px',
                    }}
                >
                    <b>Email:</b> <a href={`mailto:${email}`}>{email}</a>
                </Typography>
            )}
            {lawyerPhone && lawyerPhone !== 'null' && (
                <Typography
                    variant="body2"
                    color="text.primary"
                    style={{
                        fontSize: isMobile ? '12px' : '14px',
                        lineHeight: isMobile ? '16px' : '20px',
                    }}
                >
                    <b>Phone number:</b> +{decodeURIComponent(lawyerPhone?.slice(1))}
                </Typography>
            )}
        </Box>
    );
}

function ClientQuoteCommon({
    title,
    message,
    descriptionBoxStyles,
}: {
    title: string;
    message: string;
    descriptionBoxStyles: React.CSSProperties;
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchParams] = useSearchParams();
    const logoFileId = searchParams.get('logoUrl');

    return (
        <Box sx={commonStyles.container}>
            <Box sx={descriptionBoxStyles}>
                <MobileLogo isMobile={isMobile} logoFileId={logoFileId} />
                <Box>
                    <Typography variant={isMobile ? 'h3' : 'h1'}>{title}</Typography>
                    <Typography
                        variant="body2"
                        color="text.primary"
                        style={{
                            marginTop: '8px',
                            fontSize: isMobile ? '12px' : '14px',
                            lineHeight: isMobile ? '16px' : '20px',
                        }}
                    >
                        {message}
                    </Typography>
                    <LawyerInfo />
                </Box>
            </Box>
        </Box>
    );
}

export function ClientResponseContent({
    emailResponseAction,
    status,
    failedMessage,
}: {
    emailResponseAction: string;
    status: string;
    failedMessage?: string;
}) {
    const [isDeclined, setIsDeclined] = useState<boolean>(false);
    const [isCallRequested, setIsCallRequested] = useState<boolean>(false);
    const [errorInApiResponse, setErrorInApiResponse] = useState<ApprovalResponse | null>(null);
    const [declineReason, setDeclineReason] = useState<string>('');
    const [callRequestReason, setCallRequestReason] = useState<string>('');
    const queryParams = new URLSearchParams(location.search);
    const intakeTemplateId = queryParams.get('intakeTemplateId');
    const lawFirmId = queryParams.get('lawFirmId');
    const lawFirmName = queryParams.get('lawfirmName');
    const lawyerPhone = queryParams.get('lawyerPhone');
    const email = queryParams.get('lawyerEmail');
    const lawyerName = queryParams.get('lawyerName');
    const prospectCreatedAs = queryParams.get('prospectCreatedAs');
    const quoteTemplateId = queryParams.get('quoteTemplateId');
    const templateType = queryParams.get('templateType');
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [intakeFormStatus, fetchIntakeFormStatus] = useClientApprovalPageStore((state) => [
        state.intakeFormStatus,
        state.fetchIntakeFormStatus,
    ]);

    const [searchParams] = useSearchParams();
    const { instanceId, prospectId } = useParams();
    const action = searchParams.get('Action');
    const logoFileId = searchParams.get('logoUrl');

    const descriptionBoxStyles: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'row',
        gap: logoFileId && isMobile ? '16px' : '0px',
    };

    const mobileButtons: React.CSSProperties = {
        width: '100%',
        textTransform: 'none',
        padding: '8px 20px',
        fontSize: '14px',
        height: '40px',
    };

    const handleDecline = async () => {
        if (!instanceId || !prospectId) return;
        try {
            await getClientApprovalStatus(
                instanceId,
                prospectId,
                parseClientApprovalAction(action),
                { reason: declineReason },
            );
            setIsDeclined(true);
        } catch (error: any) {
            if (error?.response?.data?.Status === 'ClientAlreadyAccepted') {
                await fetchIntakeFormStatus(instanceId, prospectId);
            }
            console.error(error);
            setErrorInApiResponse(error?.response?.data);
        }
    };
    const handleRequestCall = async () => {
        if (!instanceId || !prospectId) return;
        try {
            await getClientApprovalStatus(
                instanceId,
                prospectId,
                parseClientApprovalAction(action),
                { reason: callRequestReason },
            );
            setIsCallRequested(true);
        } catch (error: any) {
            console.error(error);
            if (error?.response?.data?.Status === 'ClientAlreadyAccepted') {
                await fetchIntakeFormStatus(instanceId, prospectId);
            }
            setErrorInApiResponse(error?.response?.data);
        }
    };

    const declinedReason = (
        <Box sx={commonStyles.container}>
            <Box sx={descriptionBoxStyles}>
                <MobileLogo isMobile={isMobile} logoFileId={logoFileId} />
                <Box>
                    <Typography variant={isMobile ? 'h3' : 'h1'}>Decline reason</Typography>
                    <Typography
                        variant="body2"
                        color="text.primary"
                        style={{
                            marginTop: '8px',
                            fontSize: isMobile ? '12px' : '14px',
                            lineHeight: isMobile ? '16px' : '20px',
                        }}
                    >
                        Describe the decline reason to be sent to {lawFirmName || 'law firm'}. You
                        can leave it empty as well.
                    </Typography>
                    <LawyerInfo />
                </Box>
            </Box>

            <TextField
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="e.g. “I have changed my plans”; “I don’t like the pricing” etc."
            />
            <Box
                sx={{
                    display: 'flex',
                    gap: '10px',
                    width: '100%',
                    justifyContent: isMobile ? 'space-between' : 'flex-start',
                }}
            >
                <Button variant="outlined" sx={isMobile ? mobileButtons : commonStyles.buttonText}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleDecline}
                    sx={isMobile ? mobileButtons : commonStyles.buttonText}
                >
                    Confirm
                </Button>
            </Box>
        </Box>
    );

    const quoteDeclined = (
        <Box sx={commonStyles.container}>
            <Box sx={descriptionBoxStyles}>
                <MobileLogo isMobile={isMobile} logoFileId={logoFileId} />
                <Box>
                    <Typography variant={isMobile ? 'h3' : 'h1'}>Quote declined</Typography>
                    <Typography
                        variant="body2"
                        color="text.primary"
                        style={{
                            marginTop: '8px',
                            fontSize: isMobile ? '12px' : '14px',
                            lineHeight: isMobile ? '16px' : '20px',
                        }}
                    >
                        You have successfully declined the quote.
                    </Typography>
                    <LawyerInfo />
                </Box>
            </Box>
        </Box>
    );

    const callRequested = (
        <Box sx={commonStyles.container}>
            <Box sx={descriptionBoxStyles}>
                <MobileLogo isMobile={isMobile} logoFileId={logoFileId} />
                <Box>
                    <Typography variant={isMobile ? 'h3' : 'h1'}>
                        Call has been requested
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.primary"
                        style={{
                            marginTop: '8px',
                            fontSize: isMobile ? '12px' : '14px',
                            lineHeight: isMobile ? '16px' : '20px',
                        }}
                    >
                        Your request for a call has been submitted. Our office will be in touch with
                        you as soon as possible.
                    </Typography>
                    <LawyerInfo />
                </Box>
            </Box>
        </Box>
    );

    const requestACall = (
        <Box sx={commonStyles.container}>
            <Box sx={descriptionBoxStyles}>
                <MobileLogo isMobile={isMobile} logoFileId={logoFileId} />
                <Box>
                    <Typography variant={isMobile ? 'h3' : 'h1'}>Request a call</Typography>
                    <Typography
                        variant="body2"
                        color="text.primary"
                        style={{
                            marginTop: '8px',
                            fontSize: isMobile ? '12px' : '14px',
                            lineHeight: isMobile ? '16px' : '20px',
                        }}
                    >
                        Please state the reason for the call. You can leave it empty as well.
                    </Typography>
                    <LawyerInfo />
                </Box>
            </Box>
            <TextField
                value={callRequestReason}
                onChange={(e) => setCallRequestReason(e.target.value)}
                fullWidth
                multiline
                rows={3}
                sx={isMobile ? textFieldRootMobile : undefined}
                placeholder="e.g. “I need to discuss some parts of the quote”; “I have questions regarding the pricing” etc."
            />
            <Box
                sx={{
                    display: 'flex',
                    gap: '10px',
                    width: '100%',
                    justifyContent: isMobile ? 'space-between' : 'flex-start',
                }}
            >
                <Button variant="outlined" sx={isMobile ? mobileButtons : commonStyles.buttonText}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleRequestCall}
                    sx={isMobile ? mobileButtons : commonStyles.buttonText}
                >
                    Confirm
                </Button>
            </Box>
        </Box>
    );

    const QuoteAccepted = () => (
        <Box sx={commonStyles.container}>
            <Box sx={descriptionBoxStyles}>
                <MobileLogo isMobile={isMobile} logoFileId={logoFileId} />
                <Box>
                    <Typography variant={isMobile ? 'h3' : 'h1'}>Quote accepted</Typography>
                    <Typography
                        variant="body2"
                        color="text.primary"
                        style={{
                            marginTop: '8px',
                            fontSize: isMobile ? '12px' : '14px',
                            lineHeight: isMobile ? '16px' : '20px',
                        }}
                    >
                        You have successfully accepted the quote and {lawFirmName || 'the law firm'}{' '}
                        has been notified.
                        <br />
                        <br />
                        Proceed to the intake form to complete the necessary information for{' '}
                        {lawFirmName || 'the law firm'} to begin working on your file.
                    </Typography>
                    <LawyerInfo />
                </Box>
            </Box>

            <Button
                variant="contained"
                sx={{
                    marginTop: '16px',
                    padding: '8px 24px',
                    width: isMobile ? '100%' : 'fit-content',
                    textTransform: 'none',
                }}
                onClick={() =>
                    navigate(
                        `/intake/view/${intakeTemplateId}/${lawFirmId}?instanceId=${instanceId}&prospectId=${prospectId}&lawyerName=${lawyerName}&lawyerEmail=${email}&lawyerPhone=${lawyerPhone}&prospectCreatedAs=${prospectCreatedAs}&quoteTemplateId=${quoteTemplateId}&templateType=${templateType}`,
                    )
                }
            >
                Go to intake form
            </Button>
        </Box>
    );

    const quoteAlreadyAccepted = (
        <Box sx={commonStyles.container}>
            <Box sx={descriptionBoxStyles}>
                <MobileLogo isMobile={isMobile} logoFileId={logoFileId} />
                <Box>
                    <Typography variant={isMobile ? 'h3' : 'h1'}>
                        Action has already been taken
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.primary"
                        style={{
                            marginTop: '8px',
                            fontSize: isMobile ? '12px' : '14px',
                            lineHeight: isMobile ? '16px' : '20px',
                        }}
                    >
                        You have already accepted the quote.
                    </Typography>
                    <LawyerInfo />
                </Box>
            </Box>

            {(!intakeFormStatus || intakeFormStatus.Status === null) && (
                <Button
                    variant="contained"
                    sx={{
                        marginTop: '16px',
                        padding: '8px 24px',
                        width: isMobile ? '100%' : 'fit-content',
                        textTransform: 'none',
                    }}
                    onClick={() =>
                        navigate(
                            `/intake/view/${intakeTemplateId}/${lawFirmId}?instanceId=${instanceId}&prospectId=${prospectId}&lawyerName=${lawyerName}&lawyerEmail=${email}&lawyerPhone=${lawyerPhone}&prospectCreatedAs=${prospectCreatedAs}&quoteTemplateId=${quoteTemplateId}&templateType=${templateType}`,
                        )
                    }
                >
                    Go to intake form
                </Button>
            )}
        </Box>
    );

    const handleActionRender = (status: string, emailResponseAction: string): ReactNode => {
        switch (status) {
            case 'Success':
                if (emailResponseAction === 'Accepted') {
                    return <QuoteAccepted />;
                }
                if (emailResponseAction === 'RequestCall') {
                    return isCallRequested
                        ? callRequested
                        : errorInApiResponse
                          ? handleActionRender(
                                errorInApiResponse.Status || 'Failed',
                                emailResponseAction,
                            )
                          : requestACall;
                }
                if (emailResponseAction === 'Declined') {
                    return isDeclined
                        ? quoteDeclined
                        : errorInApiResponse
                          ? handleActionRender(
                                errorInApiResponse.Status || 'Failed',
                                emailResponseAction,
                            )
                          : declinedReason;
                }
                return (
                    <ClientQuoteCommon
                        title="Invalid Action"
                        message="Invalid Action Received, allowed actions are a. Accept  b. Decline"
                        descriptionBoxStyles={descriptionBoxStyles}
                    />
                );

            case 'ClientAlreadyAccepted':
                return quoteAlreadyAccepted;

            case 'ClientAlreadyDeclined':
                return (
                    <ClientQuoteCommon
                        title="Action has already been taken"
                        message="You have already declined the quote."
                        descriptionBoxStyles={descriptionBoxStyles}
                    />
                );
            case 'ClientAlreadyRequestedACall':
                return (
                    <ClientQuoteCommon
                        title="Call requested has not been registered"
                        message="You have already requested a call for this quote."
                        descriptionBoxStyles={descriptionBoxStyles}
                    />
                );
            case 'NewManualQuoteSent':
                return (
                    <ClientQuoteCommon
                        title="Quote Expired"
                        message="A new version of the quote has been sent to you and this quote is no longer valid."
                        descriptionBoxStyles={descriptionBoxStyles}
                    />
                );
            case 'Expired':
                return (
                    <ClientQuoteCommon
                        title="Quote Expired"
                        message="The quote has passed its expiry time."
                        descriptionBoxStyles={descriptionBoxStyles}
                    />
                );
            default:
                return (
                    <ClientQuoteCommon
                        title={
                            APPROVAL_STATUS_TITLES[status as ApprovalResponseStatus]
                                ? APPROVAL_STATUS_TITLES[status as ApprovalResponseStatus]
                                : 'Something went wrong!'
                        }
                        message={failedMessage || ''}
                        descriptionBoxStyles={descriptionBoxStyles}
                    />
                );
        }
    };

    return handleActionRender(status, emailResponseAction);
}
